import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Download, Calendar as CalendarIcon, TrendingUp, Users, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { format, subMonths } from "date-fns";
import { db } from "../../firebase/config";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

interface BorrowRecord {
  id: string;
  bookId: string;
  bookTitle: string;
  memberId: string;
  lookupId: string;
  memberName: string;
  borrowDate: string;
  dueDate: string;
  returnDate?: string;
  status: "borrowed" | "returned";
  fineAmount: number;
  renewCount: number;
  maxRenewals: number;
}

interface Book {
  id: string;
  title: string;
  author: string;
  isbn: string;
  category: string;
  shelf: string;
  quantity: number;
  available: number;
  status: "available" | "borrowed" | "reserved";
  coverImage?: string;
  description?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

interface Member {
  id: string;
  name: string;
  memberId: string;
  role: "student" | "staff";
  borrowedBooks: number;
  maxBorrowLimit: number;
  createdAt?: Date;
}

interface MostBorrowedBook {
  id: string;
  title: string;
  author: string;
  category: string;
  borrowCount: number;
  currentlyBorrowed: number;
}

interface OverdueBook {
  id: string;
  title: string;
  memberName: string;
  memberId: string;
  dueDate: string;
  overdueDays: number;
  fineAmount: number;
}

interface ActiveMember {
  id: string;
  name: string;
  memberId: string;
  role: string;
  booksCurrentlyBorrowed: number;
  totalBooksBorrowed: number;
  joinDate: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

export const LibraryReports = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reportType, setReportType] = useState("most-borrowed");
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({
    from: subMonths(new Date(), 6),
    to: new Date()
  });
  const [records, setRecords] = useState<BorrowRecord[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [, setSchoolID] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const today = new Date(2025, 9, 15); // October 15, 2025

  // Auth and Data Fetching - Reports need ALL data for accurate statistics
  useEffect(() => {
    let unsubscribeAuth: (() => void) | undefined;
    let unsubscribeStudents: (() => void) | undefined;
    let unsubscribeTeachers: (() => void) | undefined;
    let unsubscribeBooks: (() => void) | undefined;
    let unsubscribeRecords: (() => void) | undefined;

    const auth = getAuth();
    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

        let selectedAffiliation = defaultAffiliation && (defaultAffiliation.role === "admin" || defaultAffiliation.role === "school_staff") && defaultAffiliation.schoolID
          ? defaultAffiliation
          : affiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;

        if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
          const retryAffiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          selectedAffiliation = retryAffiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;

          if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
            throw new Error(
              `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, defaultAffiliation=${JSON.stringify(
                defaultAffiliation
              )}, schoolID=${claimSchoolID}`
            );
          }
        }

        const selectedSchoolID = selectedAffiliation.schoolID || null;
        setSchoolID(selectedSchoolID);

        if (selectedSchoolID) {
          // Fetch Students
          const studentsRef = collection(db, `schools/${selectedSchoolID}/students`);
          const studentsQuery = query(studentsRef, orderBy("createdAt", "desc"), limit(1000));
          unsubscribeStudents = onSnapshot(studentsQuery, (snapshot) => {
            const studentData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                name: `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Unknown",
                memberId: data.registrationNumber || "",
                role: "student" as const,
                borrowedBooks: data.borrowedBooks || 0,
                maxBorrowLimit: 3,
                createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
              } as Member;
            });
            setMembers((prev) => [...prev.filter((m) => m.role === "staff"), ...studentData]);
          });

          // Fetch Teachers/Staff
          const teachersRef = collection(db, `schools/${selectedSchoolID}/teachers`);
          const teachersQuery = query(teachersRef, orderBy("timestamp", "desc"), limit(1000));
          unsubscribeTeachers = onSnapshot(teachersQuery, (snapshot) => {
            const teacherData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: data.teacherId || doc.id,
                name: `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Unknown",
                memberId: data.employeeId || "",
                role: "staff" as const,
                borrowedBooks: data.borrowedBooks || 0,
                maxBorrowLimit: 5,
                createdAt: data.timestamp ? data.timestamp.toDate() : new Date(),
              } as Member;
            });
            setMembers((prev) => [...prev.filter((m) => m.role === "student"), ...teacherData]);
          });

          // Fetch All Books
          const booksRef = collection(db, `schools/${selectedSchoolID}/books`);
          const booksQuery = query(booksRef, orderBy("title"), limit(1000));
          unsubscribeBooks = onSnapshot(booksQuery, (snapshot) => {
            const bookData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                title: data.title,
                author: data.author,
                isbn: data.isbn,
                category: data.category,
                shelf: data.shelf,
                quantity: data.quantity,
                available: data.available,
                status: data.status,
                coverImage: data.coverImage,
                description: data.description,
                createdAt: data.createdAt,
                updatedAt: data.updatedAt,
              } as Book;
            });
            setBooks(bookData);
          });

          // Fetch ALL Borrow Records - Reports need all data for accurate statistics
          const recordsRef = collection(db, `schools/${selectedSchoolID}/borrowRecords`);
          const recordsQuery = query(recordsRef, orderBy("borrowDate", "desc"), limit(5000));
          unsubscribeRecords = onSnapshot(recordsQuery, (snapshot) => {
            const recordData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                bookId: data.bookId,
                bookTitle: data.bookTitle,
                memberId: data.memberId,
                lookupId: data.lookupId || data.memberId,
                memberName: data.memberName,
                borrowDate: data.borrowDate.toDate().toISOString().split("T")[0],
                dueDate: data.dueDate.toDate().toISOString().split("T")[0],
                returnDate: data.returnDate ? data.returnDate.toDate().toISOString().split("T")[0] : undefined,
                status: data.status,
                fineAmount: data.fineAmount || 0,
                renewCount: data.renewCount || 0,
                maxRenewals: data.maxRenewals || 2,
              } as BorrowRecord;
            });
            setRecords(recordData);
          });

          setLoading(false);
        } else {
          throw new Error("No school affiliation found.");
        }
      } catch (error: any) {
        console.error("Error verifying user claims:", error);
        setLoading(false);
        setError("Failed to verify permissions. Please contact support.");
        toast({
          title: "Missing Permissions",
          description: "You lack the necessary permissions to view reports. Please contact support.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
    });

    return () => {
      unsubscribeAuth?.();
      unsubscribeStudents?.();
      unsubscribeTeachers?.();
      unsubscribeBooks?.();
      unsubscribeRecords?.();
    };
  }, [navigate, toast]);

  const filteredRecords = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return records;
    const from = dateRange.from;
    const to = dateRange.to;
    return records.filter(r => {
      const borrowDate = new Date(r.borrowDate);
      return borrowDate >= from && borrowDate <= to;
    });
  }, [records, dateRange]);

  const mostBorrowedBooks = useMemo((): MostBorrowedBook[] => {
    const borrowCounts: Record<string, number> = {};
    const currentBorrowed: Record<string, number> = {};
    filteredRecords.forEach(r => {
      borrowCounts[r.bookId] = (borrowCounts[r.bookId] || 0) + 1;
    });
    records.filter(r => r.status === 'borrowed').forEach(r => {
      currentBorrowed[r.bookId] = (currentBorrowed[r.bookId] || 0) + 1;
    });
    return Object.entries(borrowCounts)
      .map(([bookId, borrowCount]) => {
        const book = books.find(b => b.id === bookId);
        if (!book) return null;
        return {
          id: bookId,
          title: book.title,
          author: book.author,
          category: book.category,
          borrowCount,
          currentlyBorrowed: currentBorrowed[bookId] || 0
        };
      })
      .filter((b): b is MostBorrowedBook => b !== null)
      .sort((a, b) => b.borrowCount - a.borrowCount)
      .slice(0, 5);
  }, [filteredRecords, records, books]);

  const overdueBooks = useMemo((): OverdueBook[] => {
    return records
      .filter(r => r.status === 'borrowed' && new Date(r.dueDate) < today)
      .map(r => {
        const overdueDays = Math.floor((today.getTime() - new Date(r.dueDate).getTime()) / (1000 * 60 * 60 * 24));
        const fineAmount = overdueDays * 1.00;
        return {
          id: r.id,
          title: r.bookTitle,
          memberName: r.memberName,
          memberId: r.memberId,
          dueDate: r.dueDate,
          overdueDays,
          fineAmount
        };
      })
      .sort((a, b) => b.overdueDays - a.overdueDays);
  }, [records]);

  const activeMembers = useMemo((): ActiveMember[] => {
    const totalBorrows: Record<string, number> = {};
    const currentBorrowed: Record<string, number> = {};
    filteredRecords.forEach(r => {
      totalBorrows[r.memberId] = (totalBorrows[r.memberId] || 0) + 1;
    });
    records.filter(r => r.status === 'borrowed').forEach(r => {
      currentBorrowed[r.memberId] = (currentBorrowed[r.memberId] || 0) + 1;
    });
    return members
      .filter(m => (currentBorrowed[m.memberId] || 0) > 0 || (totalBorrows[m.memberId] || 0) > 0)
      .map(m => ({
        id: m.id,
        name: m.name,
        memberId: m.memberId,
        role: m.role === 'student' ? 'Student' : 'Staff',
        booksCurrentlyBorrowed: currentBorrowed[m.memberId] || 0,
        totalBooksBorrowed: totalBorrows[m.memberId] || 0,
        joinDate: m.createdAt ? format(m.createdAt, 'yyyy-MM-dd') : 'Unknown'
      }))
      .sort((a, b) => b.totalBooksBorrowed - a.totalBooksBorrowed)
      .slice(0, 10);
  }, [filteredRecords, records, members]);

  const borrowingTrendData = useMemo(() => {
    const from = dateRange.from || subMonths(today, 6);
    const to = dateRange.to || today;
    const monthlyBorrows: Record<string, number> = {};
    const monthlyReturns: Record<string, number> = {};
    filteredRecords.forEach(r => {
      const date = new Date(r.borrowDate);
      if (date >= from && date <= to) {
        const monthKey = format(date, 'MMM');
        monthlyBorrows[monthKey] = (monthlyBorrows[monthKey] || 0) + 1;
      }
    });
    records.filter(r => r.returnDate).forEach(r => {
      const retDate = new Date(r.returnDate!);
      if (retDate >= from && retDate <= to) {
        const monthKey = format(retDate, 'MMM');
        monthlyReturns[monthKey] = (monthlyReturns[monthKey] || 0) + 1;
      }
    });
    const months = [...new Set([...Object.keys(monthlyBorrows), ...Object.keys(monthlyReturns)])].sort();
    return months.map(month => ({
      month,
      borrows: monthlyBorrows[month] || 0,
      returns: monthlyReturns[month] || 0
    }));
  }, [filteredRecords, records, dateRange, today]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredRecords.forEach(r => {
      const book = books.find(b => b.id === r.bookId);
      if (book) {
        counts[book.category] = (counts[book.category] || 0) + 1;
      }
    });
    return Object.entries(counts);
  }, [filteredRecords, books]);

  const categoryColors: Record<string, string> = {
    Fiction: "#2563EB",
    'Non-Fiction': "#10B981",
    Science: "#F59E0B",
    History: "#6B7280",
    Technology: "#3B82F6",
  };

  const categoryData = useMemo(() =>
    categoryCounts
      .map(([name, value]) => ({
        name,
        value,
        color: categoryColors[name as keyof typeof categoryColors] || "#6B7280"
      }))
      .slice(0, 5)
    , [categoryCounts, categoryColors]);

  const totalBooks = books.length;
  const activeMembersCount = members.filter(m => m.borrowedBooks > 0).length;
  const booksOut = records.filter(r => r.status === 'borrowed').length;
  const overdueCount = overdueBooks.length;

  const exportReport = () => {
    let csvContent = "";
    let filename = "";

    switch (reportType) {
      case "most-borrowed":
        csvContent = [
          ["Title", "Author", "Category", "Borrow Count", "Currently Borrowed"].join(","),
          ...mostBorrowedBooks.map(book => [
            book.title,
            book.author,
            book.category,
            book.borrowCount,
            book.currentlyBorrowed
          ].join(","))
        ].join("\n");
        filename = "most_borrowed_books";
        break;

      case "overdue":
        csvContent = [
          ["Title", "Member", "Member ID", "Due Date", "Overdue Days", "Fine Amount"].join(","),
          ...overdueBooks.map(book => [
            book.title,
            book.memberName,
            book.memberId,
            book.dueDate,
            book.overdueDays,
            book.fineAmount.toFixed(2)
          ].join(","))
        ].join("\n");
        filename = "overdue_books";
        break;

      case "active-members":
        csvContent = [
          ["Name", "Member ID", "Role", "Currently Borrowed", "Total Borrowed", "Join Date"].join(","),
          ...activeMembers.map(member => [
            member.name,
            member.memberId,
            member.role,
            member.booksCurrentlyBorrowed,
            member.totalBooksBorrowed,
            member.joinDate
          ].join(","))
        ].join("\n");
        filename = "active_members";
        break;

      case "analytics":
        csvContent = [
          ["Metric", "Value"],
          ["Total Books", totalBooks],
          ["Active Members", activeMembersCount],
          ["Books Out", booksOut],
          ["Overdue", overdueCount]
        ].join("\n");
        filename = "library_stats";
        break;
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getRankBadge = (index: number) => {
    const badgeClass = index < 3
      ? "bg-[#F59E0B] dark:bg-[#FBBF24] hover:bg-[#D97706] dark:hover:bg-[#F59E0B] text-white"
      : "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white";
    return (
      <Badge className={`text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate ${badgeClass}`}>
        #{index + 1}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    const badgeClass = role === "Staff"
      ? "bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] text-white"
      : "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white";
    return (
      <Badge className={`text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate ${badgeClass}`}>
        {role}
      </Badge>
    );
  };

  const getOverdueBadge = (days: number) => {
    return (
      <Badge className="text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate bg-[#EF4444] dark:bg-[#F87171] hover:bg-[#DC2626] dark:hover:bg-[#EF4444] text-white">
        {days} days
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <p className="text-foreground text-center">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-2 px-4 rounded-full flex items-center gap-2">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto {
            font-family: 'Roboto', sans-serif;
          }
          /* Mobile-only fix – prevents black overscroll bar */
          @media (max-width: 767px) {
            body { overflow-x: hidden; }
          }
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          /* Mobile-only full-width scroll container */
          @media (max-width: 767px) {
            .mobile-table-container {
              width: 100vw;
              margin-left: calc(-50vw + 50%);
            }
          }
        `}
      </style>
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="space-y-6">
          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="w-full sm:w-64 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                  <SelectItem value="most-borrowed" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                    Most Borrowed Books
                  </SelectItem>
                  <SelectItem value="overdue" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                    Overdue Books
                  </SelectItem>
                  <SelectItem value="active-members" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                    Active Members
                  </SelectItem>
                  <SelectItem value="analytics" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">
                    Analytics Dashboard
                  </SelectItem>
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="m-2 w-full sm:w-auto justify-start text-left font-medium bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "LLL dd, y")} -{" "}
                          {format(dateRange.to, "LLL dd, y")}
                        </>
                      ) : (
                        format(dateRange.from, "LLL dd, y")
                      )
                    ) : (
                      "Pick a date range"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range) => setDateRange({ from: range?.from, to: range?.to })}
                    numberOfMonths={2}
                    className="bg-[#FFFFFF] dark:bg-[#020817] text-black dark:text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Button
              variant="outline"
              onClick={exportReport}
              className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
            >
              <Download className="h-4 w-4 mr-2 text-muted-foreground" />
              Export Report
            </Button>
          </div>

          {/* Report Content */}
          {reportType === "most-borrowed" && (
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-black dark:text-white">
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />
                  Most Borrowed Books
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-full overflow-auto no-scrollbar">
                  <div className="mobile-table-container overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                          <TableHead className="w-[80px] text-black dark:text-white">Rank</TableHead>
                          <TableHead className="min-w-[150px] text-black dark:text-white">Title</TableHead>
                          <TableHead className="min-w-[120px] text-black dark:text-white">Author</TableHead>
                          <TableHead className="min-w-[100px] text-black dark:text-white">Category</TableHead>
                          <TableHead className="w-[120px] text-center text-black dark:text-white">Total Borrows</TableHead>
                          <TableHead className="w-[120px] text-center text-black dark:text-white">Currently Out</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {mostBorrowedBooks.map((book, index) => (
                          <TableRow key={book.id} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                            <TableCell className="font-bold py-2">{getRankBadge(index)}</TableCell>
                            <TableCell className="font-medium text-black dark:text-white py-2">
                              <div className="truncate max-w-[150px]">{book.title}</div>
                            </TableCell>
                            <TableCell className="text-black dark:text-white py-2">
                              <div className="truncate max-w-[120px]">{book.author}</div>
                            </TableCell>
                            <TableCell className="text-black dark:text-white py-2">
                              <div className="truncate max-w-[100px]">{book.category}</div>
                            </TableCell>
                            <TableCell className="text-center font-bold text-black dark:text-white py-2">{book.borrowCount}</TableCell>
                            <TableCell className="text-center text-black dark:text-white py-2">{book.currentlyBorrowed}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {reportType === "overdue" && (
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-black dark:text-white">
                  <Clock className="h-5 w-5 text-[#EF4444] dark:text-[#F87171]" />
                  Overdue Books Report
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-full overflow-auto no-scrollbar">
                  <div className="mobile-table-container overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                          <TableHead className="min-w-[150px] text-black dark:text-white">Book Title</TableHead>
                          <TableHead className="min-w-[150px] text-black dark:text-white">Member</TableHead>
                          <TableHead className="w-[120px] text-black dark:text-white">Due Date</TableHead>
                          <TableHead className="w-[120px] text-center text-black dark:text-white">Days Overdue</TableHead>
                          <TableHead className="w-[100px] text-right text-black dark:text-white">Fine Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {overdueBooks.map((book) => (
                          <TableRow key={book.id} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                            <TableCell className="font-medium text-black dark:text-white py-2">
                              <div className="truncate max-w-[150px]">{book.title}</div>
                            </TableCell>
                            <TableCell className="py-2">
                              <div>
                                <div className="font-medium text-black dark:text-white truncate max-w-[150px]">{book.memberName}</div>
                                <div className="text-sm text-muted-foreground truncate max-w-[150px]">{book.memberId}</div>
                              </div>
                            </TableCell>
                            <TableCell className="text-[#EF4444] dark:text-[#F87171] py-2">{book.dueDate}</TableCell>
                            <TableCell className="text-center py-2">{getOverdueBadge(book.overdueDays)}</TableCell>
                            <TableCell className="text-right font-bold text-[#EF4444] dark:text-[#F87171] py-2">
                              ${book.fineAmount.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {reportType === "active-members" && (
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg font-semibold text-black dark:text-white">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  Active Members Report
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-full overflow-auto no-scrollbar">
                  <div className="mobile-table-container overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                          <TableHead className="min-w-[150px] text-black dark:text-white">Member</TableHead>
                          <TableHead className="w-[100px] text-black dark:text-white">Role</TableHead>
                          <TableHead className="w-[120px] text-center text-black dark:text-white">Currently Borrowed</TableHead>
                          <TableHead className="w-[120px] text-center text-black dark:text-white">Total Borrowed</TableHead>
                          <TableHead className="w-[120px] text-black dark:text-white">Join Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeMembers.map((member) => (
                          <TableRow key={member.id} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                            <TableCell className="py-2">
                              <div>
                                <div className="font-medium text-black dark:text-white truncate max-w-[150px]">{member.name}</div>
                                <div className="text-sm text-muted-foreground truncate max-w-[150px]">{member.memberId}</div>
                              </div>
                            </TableCell>
                            <TableCell className="py-2">{getRoleBadge(member.role)}</TableCell>
                            <TableCell className="text-center font-bold text-black dark:text-white py-2">
                              {member.booksCurrentlyBorrowed}
                            </TableCell>
                            <TableCell className="text-center text-black dark:text-white py-2">{member.totalBooksBorrowed}</TableCell>
                            <TableCell className="text-black dark:text-white py-2">{member.joinDate}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {reportType === "analytics" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-black dark:text-white">Borrowing Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={borrowingTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" strokeOpacity={0.5} />
                      <XAxis dataKey="month" tick={{ fill: '#1F2937' }} className="font-roboto text-sm" />
                      <YAxis tick={{ fill: '#1F2937' }} className="font-roboto text-sm" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          borderColor: '#E2E8F0',
                          borderRadius: '0.375rem',
                          color: '#000000'
                        }}
                      />
                      <Bar dataKey="borrows" fill="#2563EB" name="Borrows" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="returns" fill="#10B981" name="Returns" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-black dark:text-white">Popular Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          borderColor: '#E2E8F0',
                          borderRadius: '0.375rem',
                          color: '#000000'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                    {categoryData.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-gray-600 dark:text-gray-300">{entry.name}: {entry.value}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  );
};